/**
 * Google Maps — shared configuration and the auth-failure store.
 *
 * Every map surface reads its key, map id and default centre from here so that all
 * `<APIProvider>` mounts agree on one parameter set (see `components/maps/GoogleMapsProvider`).
 *
 * The `gm_authFailure` handling below exists because a *rejected* key is otherwise
 * invisible: the bootstrap script loads fine, `useApiLoadingStatus()` reports LOADED,
 * our markers draw, and no tile ever arrives — a grey slab that looks identical to a
 * layout bug. Google's only signal is a call to `window.gm_authFailure`, which
 * @vis.gl/react-google-maps does not touch.
 */

import { useSyncExternalStore } from 'react'

/** Browser key. Baked into the bundle at build time, so it must be referrer-restricted. */
export const GOOGLE_MAPS_API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY ?? ''

/**
 * Cloud-configured map style id. Required for `<AdvancedMarker>`; also means
 * `MapOptions.styles` is inert, which is fine — this dashboard has no dark mode.
 */
export const GOOGLE_MAPS_MAP_ID = 'bf51a910020fa25a'

/** Fallback centre when nothing else is known. */
export const RIYADH_CENTER: google.maps.LatLngLiteral = { lat: 24.7136, lng: 46.6753 }

// ─── gm_authFailure store ────────────────────────────────────────────────────
//
// Module-level rather than a hook effect, deliberately:
//  - the failure is one-shot and page-wide, so a map mounting *after* it fires still
//    has to see it — the state has to outlive every subscriber;
//  - `window.gm_authFailure` is a single global, so per-instance install/cleanup would
//    let the last unmounting map delete a handler another map still needs.
// Same shape as the `auth:logout` window listener in `store/authStore.ts`.

let authFailed = false
const listeners = new Set<() => void>()

declare global {
  interface Window {
    gm_authFailure?: () => void
  }
}

if (typeof window !== 'undefined') {
  const previous = window.gm_authFailure
  window.gm_authFailure = () => {
    previous?.()
    if (authFailed) return
    authFailed = true
    // Google logs its own `Google Maps JavaScript API error: <code>` line just before
    // this fires; that code (RefererNotAllowedMapError, ApiNotActivatedMapError,
    // BillingNotEnabledMapError, …) names the exact cause and is fixed in Google Cloud.
    console.error(
      '[googleMaps] Google rejected the Maps API key. See the "Google Maps JavaScript API error" line above for the reason.'
    )
    listeners.forEach((listener) => listener())
  }
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

function getSnapshot(): boolean {
  return authFailed
}

/**
 * `true` once Google has rejected the API key for this page. Never returns to `false` —
 * auth is settled for the page's lifetime, so surfaces must not offer a retry for it.
 */
export function useGoogleMapsAuthFailed(): boolean {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot)
}
